/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-10
 */

// Add third-party dependencies.
import { Box, Paper, Skeleton, Divider } from '@mui/material';

/**
 * Props for the ProfileSkeleton component.
 */
interface ProfileSkeletonProps {
  /**
   * Whether to show the edit button skeleton.
   * @default true
   */
  showEditButton?: boolean;
  /**
   * Number of information sections to show skeletons for.
   * @default 4
   */
  sectionCount?: number;
}

/**
 * ProfileSkeleton component that displays a loading skeleton for user profile information.
 * Provides a consistent loading experience while profile data is being fetched.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The profile skeleton component
 *
 * @example
 * // Basic usage
 * return <ProfileSkeleton />;
 *
 * // Without edit button
 * return <ProfileSkeleton showEditButton={false} />;
 *
 * // Custom section count
 * return <ProfileSkeleton sectionCount={3} />;
 */
const ProfileSkeleton: React.FC<ProfileSkeletonProps> = ({
  showEditButton = true,
  sectionCount = 4,
}) => (
  <Paper sx={{ p: 3 }}>
    {/* Header with avatar and edit button */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Skeleton variant="circular" width={64} height={64} sx={{ mr: 2 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
      {showEditButton && (
        <Skeleton
          variant="rectangular"
          width={120}
          height={36}
          sx={{ ml: 2, borderRadius: 1 }}
        />
      )}
    </Box>

    <Divider sx={{ mb: 3 }} />

    {/* Information Sections */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {Array.from({ length: sectionCount }, (_, sectionIndex) => (
        <Box key={`section-${sectionIndex}`}>
          {/* Section Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width="30%" height={24} />
          </Box>

          {/* Section Content */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from(
              { length: 2 + (sectionIndex % 2) },
              (__, fieldIndex) => (
                <Box key={`field-${sectionIndex}-${fieldIndex}`}>
                  <Skeleton variant="text" width="25%" height={16} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Box>
              )
            )}
          </Box>
        </Box>
      ))}
    </Box>

    {/* Account Timestamps */}
    <Divider sx={{ my: 3 }} />
    <Box>
      <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={16} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={16} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>
      </Box>
    </Box>
  </Paper>
);

export default ProfileSkeleton;
